import { chain } from 'lodash';
import { BASE_PATH } from 'utils/constants';
import { CityPageCityFragment, CityPageFilterFragment, RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';

export const urlSafeEncode = (...args: (string | null | undefined)[]): string =>
  chain(args).compact().join(' ').deburr().kebabCase().escape().value() || '';

export const encodeCitySlug = (cityPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>) =>
  urlSafeEncode(cityPage.cityName, cityPage.stateNameShort);
export const encodeRegionSlug = (region: Pick<RegionInfoFragment, 'name'>) => urlSafeEncode(region.name, 'metro');

export const encodeCuisineSlug = (filter?: Pick<CityPageFilterFragment, 'cuisineCategoryId' | 'dietaryFilter'>) => {
  if (!filter?.dietaryFilter && !filter?.cuisineCategoryId) return null;

  // HACK: Vegan specialty -> vegan food
  if (filter.cuisineCategoryId === 'VEGAN') {
    if (filter.dietaryFilter === 'vegan') return 'vegan-food'; // Vegan vegan food is just vegan food
    return urlSafeEncode(filter.dietaryFilter, 'vegan', 'food');
  }

  // Linguistic Note: Dietary filters make cuisines into food. Example: American Cuisine vs Vegetarian American Food
  const descriptor = filter.cuisineCategoryId && !filter.dietaryFilter ? 'cuisine' : 'food';
  return urlSafeEncode(filter.dietaryFilter, filter.cuisineCategoryId, descriptor);
};

export const encodeCityPagePath = (
  cityPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>,
  filter?: Pick<CityPageFilterFragment, 'cuisineCategoryId' | 'dietaryFilter'>
): string[] => {
  const citySlug = encodeCitySlug(cityPage);
  const cuisineSlug = encodeCuisineSlug(filter);

  return cuisineSlug ? [citySlug, cuisineSlug] : [citySlug];
};

export const encodeZipPagePath = (
  zipPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>,
  zipCode: string,
  filter?: Pick<CityPageFilterFragment, 'cuisineCategoryId' | 'dietaryFilter'>
): string[] => {
  const citySlug = encodeCitySlug(zipPage);
  const cuisineSlug = encodeCuisineSlug(filter);

  return cuisineSlug ? [citySlug, zipCode, cuisineSlug] : [citySlug, zipCode];
};

export const encodeRegionPagePath = (
  region: Pick<RegionInfoFragment, 'name'>,
  filter?: Pick<CityPageFilterFragment, 'cuisineCategoryId' | 'dietaryFilter'>
): string[] => {
  const citySlug = encodeRegionSlug(region);
  const cuisineSlug = encodeCuisineSlug(filter);

  return cuisineSlug ? [citySlug, cuisineSlug] : [citySlug];
};

export const pathToHref = (path?: string[]): string => (path?.length ? `/${path.join('/')}` : '/');
export const encodeCityPageHref = (
  cityPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>,
  filter?: Pick<CityPageFilterFragment, 'cuisineCategoryId' | 'dietaryFilter'>
): string => `${BASE_PATH}${pathToHref(encodeCityPagePath(cityPage, filter))}`;

export const encodeZipPageHref = (
  cityPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>,
  zipCode: string,
  filter?: Pick<CityPageFilterFragment, 'cuisineCategoryId' | 'dietaryFilter'>
): string => `${BASE_PATH}${pathToHref(encodeZipPagePath(cityPage, zipCode, filter))}`;

export const encodeRegionPageHref = (
  region: Pick<RegionInfoFragment, 'name'>,
  filter?: Pick<CityPageFilterFragment, 'cuisineCategoryId' | 'dietaryFilter'>
): string => `${BASE_PATH}${pathToHref(encodeRegionPagePath(region, filter))}`;
